/* eslint-disable no-unused-vars, no-unused-expressions */

class KlarnaGeoIPHandler extends HTMLElement {
  constructor () {
    super()
    this.loadingPlaceholder = null
    this.klarnaContainer = null
    this.financeContainer = null
    this.klarnaCountries = this.getKlarnaCountries()
    this.currentMarket = null
    this.isEuStore = false
  }

  getKlarnaCountries () {
    try {
      // Try to get countries from theme settings
      const settingsCountries = window.Tentbox?.klarna?.countries
      if (settingsCountries && typeof settingsCountries === 'string') {
        const countries = settingsCountries
          .split(',')
          .map(country => country.trim().toUpperCase())
          .filter(country => country.length > 0)

        if (countries.length > 0) {
          return countries
        }
      }
    } catch (e) {
      // Error reading settings, will use defaults
    }

    // Fallback to default countries
    const defaultCountries = ['IT', 'ES', 'DE', 'SE']
    return defaultCountries
  }

  connectedCallback () {
    this.loadingPlaceholder = this.previousElementSibling?.classList.contains('klarna-geoip-loading-placeholder') ? this.previousElementSibling : null
    this.klarnaContainer = this.querySelector('.klarna-app-block-container')
    this.financeContainer = this.querySelector('.finance-section-container')

    this.currentMarket = this.dataset.currentMarket?.toLowerCase()
    this.isEuStore = this.dataset.isEuStore === 'true'
    const klarnaCountriesAttr = this.dataset.klarnaCountries

    if (klarnaCountriesAttr) {
      this.klarnaCountries = klarnaCountriesAttr
        .split(',')
        .map(country => country.trim().toUpperCase())
        .filter(country => country.length > 0)
    }

    if (!this.klarnaContainer || !this.financeContainer) {
      this.showFinanceSection()
      return
    }

    if (!this.isEuStore) {
      this.showFinanceSection()
      return
    }

    if (this.dataset.financeOverridden === 'true') {
      this.showFinanceSection()
      return
    }

    if (this.currentMarket && this.shouldShowKlarnaForMarket(this.currentMarket)) {
      this.showKlarnaSection()
      return
    }

    const mappedCountryFromPath = this.getMarketMappedCountryIso(this.currentMarket)
    if (typeof mappedCountryFromPath === 'string' && !this.klarnaCountries.includes(mappedCountryFromPath)) {
      this.showFinanceSection()
      return
    }

    this.waitForStoreSelector()
  }

  getMarketMappedCountryIso (market) {
    const marketToCountryMap = {
      de: 'DE',
      fr: 'FR',
      netherlands: null,
    }
    return Object.prototype.hasOwnProperty.call(marketToCountryMap, market ?? '')
      ? marketToCountryMap[market]
      : undefined
  }

  shouldShowKlarnaForMarket (market) {
    const countryCode = this.getMarketMappedCountryIso(market)
    if (typeof countryCode === 'string') {
      return this.klarnaCountries.includes(countryCode)
    }
    return false
  }

  waitForStoreSelector () {
    let attempts = 0
    const maxAttempts = 30 // 3 seconds total (30 * 100ms) - reduced from 10s

    const checkStoreSelector = () => {
      attempts++

      if (window.tbStoreSelector && window.tbStoreSelector.country) {
        this.handleGeoIPResult(window.tbStoreSelector.country)
        return
      }

      // If store selector exists but no country yet, keep waiting (it might still be fetching)
      if (window.tbStoreSelector) {
        // After 2 seconds (20 attempts), try fallback while still waiting
        if (attempts === 20) {
          const fallbackCountry = this.getFallbackCountry()
          if (fallbackCountry) {
            this.handleGeoIPResult(fallbackCountry)
            return
          }
        }

        if (attempts < maxAttempts) {
          setTimeout(checkStoreSelector, 100)
          return
        }
      }

      // If config exists but store selector not initialized yet, keep waiting
      if (window.Tentbox && window.Tentbox.storeSelector && attempts < maxAttempts) {
        setTimeout(checkStoreSelector, 100)
        return
      }

      // Timeout or no store selector available - try fallback methods

      // Try to get country from cached data directly
      const fallbackCountry = this.getFallbackCountry()
      if (fallbackCountry) {
        this.handleGeoIPResult(fallbackCountry)
      } else {
        this.showFinanceSection()
      }
    }

    // Start checking
    checkStoreSelector()
  }

  getFallbackCountry () {
    try {
      // First check sessionStorage (matches store selector's getSessionIpInfo)
      const sessionData = sessionStorage.getItem('tentboxSessionIpInfo')
      if (sessionData) {
        const parsed = JSON.parse(sessionData)
        // Store selector sets: this.country = sessionIpInfo.country (line 230)
        const country = parsed.country_code || parsed.country
        if (country) {
          return country
        }
      }

      // Then check localStorage (matches store selector's getCachedIpInfo)
      const localData = localStorage.getItem('tentboxIpInfo')
      if (localData) {
        const parsed = JSON.parse(localData)
        // Store selector sets: this.country = cachedIpInfo.country_code || cachedIpInfo.country (line 239)
        // But localStorage format is: { data: {...}, timestamp: ... }
        const cachedIpInfo = parsed.data
        if (cachedIpInfo) {
          const country = cachedIpInfo.country_code || cachedIpInfo.country
          if (country) {
            return country
          }
        }
      }
    } catch (e) {
      // Error reading cached country data
    }

    return null
  }

  handleGeoIPResult (countryCode) {
    if (!countryCode) {
      this.showFinanceSection()
      return
    }

    const userCountry = countryCode.toUpperCase()

    if (this.klarnaCountries.includes(userCountry)) {
      this.showKlarnaSection()
    } else {
      this.showFinanceSection()
    }
  }

  waitForKlarnaToLoad (callback) {
    if (!this.klarnaContainer) {
      callback()
      return
    }

    let attempts = 0
    const maxAttempts = 40
    let stableCount = 0
    let lastElementCount = 0

    const checkKlarnaLoaded = () => {
      attempts++

      const klarnaContent = this.klarnaContainer.innerHTML.trim()
      const hasContent = klarnaContent.length > 0
      const elementCount = this.klarnaContainer.querySelectorAll('*').length
      const hasElements = elementCount > 0
      const hasKlarnaWidget = this.klarnaContainer.querySelector('[data-klarna-widget], iframe, [id*="klarna"], [class*="klarna"], klarna-placement') !== null

      const isKlarnaReady = hasContent && hasElements && hasKlarnaWidget

      if (isKlarnaReady) {
        if (elementCount === lastElementCount && elementCount > 0) {
          stableCount++
        } else {
          stableCount = 0
        }
        lastElementCount = elementCount

        if (stableCount >= 2) {
          setTimeout(() => {
            callback()
          }, 100)
          return
        }
      } else {
        stableCount = 0
        lastElementCount = 0
      }

      if (attempts >= maxAttempts) {
        setTimeout(() => {
          callback()
        }, 100)
        return
      }

      setTimeout(checkKlarnaLoaded, 30)
    }

    checkKlarnaLoaded()
  }

  clearProductPriceLoading () {
    const productPrice = this.closest('.product-price')
    if (!productPrice || !productPrice.querySelector('finance-module')) {
      productPrice?.removeAttribute('loading')
      productPrice?.removeAttribute('aria-busy')
    }
  }

  showKlarnaSection () {
    this.waitForKlarnaToLoad(() => {
      if (this.loadingPlaceholder) {
        this.loadingPlaceholder.classList.add('hidden')
      }
      this.classList.remove('hidden')
      this.classList.add('block')
      if (this.klarnaContainer) {
        this.klarnaContainer.classList.remove('hidden')
      }
      if (this.financeContainer) {
        this.financeContainer.classList.add('hidden')
      }
      this.clearProductPriceLoading()
      this.dispatchResolved('klarna')
    })
  }

  showFinanceSection () {
    setTimeout(() => {
      if (this.loadingPlaceholder) {
        this.loadingPlaceholder.classList.add('hidden')
      }
      this.classList.remove('hidden')
      this.classList.add('block')
      if (this.klarnaContainer) {
        this.klarnaContainer.classList.add('hidden')
      }
      if (this.financeContainer) {
        this.financeContainer.classList.remove('hidden')
      }
      this.clearProductPriceLoading()
      this.dispatchResolved('finance')
    }, 100)
  }

  dispatchResolved (provider) {
    this.dispatchEvent(new CustomEvent('finance:resolved', { bubbles: true, detail: { provider } }))
  }
}

class MainProduct extends HTMLElement {
  connectedCallback () {
    this.initDescriptionToggle()
    this.initAccordions()
    this.initProductIconBlocks()
  }

  initDescriptionToggle () {
    const container = this.querySelector('[data-description-container]')
    const wrapper = this.querySelector('.product-info__description-wrapper')
    const toggleButton = this.querySelector('[data-description-toggle]')
    const content = this.querySelector('[data-description-content]')
    const buttonText = this.querySelector('[data-button-text]')

    if (!toggleButton || !content || !buttonText || !container || !wrapper) {
      return
    }

    const isEmpty = !content.textContent || content.textContent.trim().length === 0
    if (isEmpty) {
      toggleButton.style.display = 'none'
      const gradient = this.querySelector('[data-description-gradient]')
      if (gradient) {
        gradient.style.display = 'none'
      }
      return
    }

    // Store whether there are specs or bullet points for later reference
    const hasSpecsOrBullets = this.querySelector('.product-info__description-specs-bullet-points-wrapper') !== null
    content.dataset.hasSpecsOrBullets = hasSpecsOrBullets

    this.checkIfTruncationNeeded(content, toggleButton)

    toggleButton.addEventListener('click', (e) => {
      e.preventDefault()
      this.handleDescriptionToggle(toggleButton, content, container, wrapper, buttonText)
    })

    toggleButton.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        this.handleDescriptionToggle(toggleButton, content, container, wrapper, buttonText)
      }
    })
  }

  handleDescriptionToggle (toggleButton, content, container, wrapper, buttonText) {
    const isExpanded = toggleButton.getAttribute('aria-expanded') === 'true'
    const readMoreText = toggleButton.dataset.readMoreTranslation
    const readLessText = toggleButton.dataset.readLessTranslation
    const hasSpecsOrBullets = content.dataset.hasSpecsOrBullets === 'true'

    if (isExpanded) {
      if (hasSpecsOrBullets) {
        content.classList.add('product-info__description-content--collapsed')
      } else {
        content.classList.add('product-info__description-content--collapsed-no-specs')
      }
      content.classList.remove('product-info__description-content--expanded')
      container.classList.remove('product-info__description--expanded')
      wrapper.classList.remove('product-info__description-wrapper--expanded')
      toggleButton.setAttribute('aria-expanded', 'false')
      buttonText.textContent = readMoreText
      this.announceToScreenReader('Description collapsed')
    } else {
      content.classList.remove('product-info__description-content--collapsed')
      content.classList.remove('product-info__description-content--collapsed-no-specs')
      content.classList.add('product-info__description-content--expanded')
      container.classList.add('product-info__description--expanded')
      wrapper.classList.add('product-info__description-wrapper--expanded')
      toggleButton.setAttribute('aria-expanded', 'true')
      buttonText.textContent = readLessText
      this.announceToScreenReader('Description expanded')
    }
  }

  initAccordions () {
    const accordionContainer = this.querySelector('[data-accordions-container]')
    if (!accordionContainer) return
    const accordionDetails = accordionContainer.querySelectorAll('[data-accordion-details]')
    accordionDetails.forEach(details => {
      const content = details.querySelector('[data-accordion-content]')
      const icon = details.querySelector('[data-accordion-icon]')
      const summary = details.querySelector('summary')
      if (!content || !icon || !summary) return
      details.dataset.isAnimating = 'false'
      summary.addEventListener('click', (e) => {
        e.preventDefault()
        if (details.dataset.isAnimating === 'true') return
        this.toggleAccordion(details, content, icon)
      })
      summary.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          if (details.dataset.isAnimating === 'true') return
          this.toggleAccordion(details, content, icon)
        }
      })
      details.open = false
      details.style.overflow = 'hidden'
      details.style.height = `${summary.offsetHeight}px`
      content.style.opacity = '0'
    })
  }

  async toggleAccordion (details, content, icon) {
    const summary = details.querySelector('summary')
    const isOpening = !details.open
    details.dataset.isAnimating = 'true'
    if (isOpening) {
      details.open = true
      const summaryHeight = summary.offsetHeight
      const contentHeight = content.offsetHeight
      const targetHeight = summaryHeight + contentHeight
      details.style.height = `${summaryHeight}px`
      const heightAnimation = details.animate([
        { height: `${summaryHeight}px` },
        { height: `${targetHeight}px` },
      ], {
        duration: 200,
        easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
        fill: 'forwards',
      })
      const contentAnimation = content.animate([
        { opacity: 0 },
        { opacity: 1 },
      ], {
        duration: 200,
        easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
        fill: 'forwards',
        delay: 100,
      })
      icon.style.transform = 'rotate(45deg)'
      await Promise.all([heightAnimation.finished, contentAnimation.finished])
    } else {
      const summaryHeight = summary.offsetHeight
      const currentHeight = details.offsetHeight
      const heightAnimation = details.animate([
        { height: `${currentHeight}px` },
        { height: `${summaryHeight}px` },
      ], {
        duration: 200,
        easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
        fill: 'forwards',
      })
      const contentAnimation = content.animate([
        { opacity: 1 },
        { opacity: 0 },
      ], {
        duration: 150,
        easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
        fill: 'forwards',
      })
      icon.style.transform = 'rotate(0deg)'
      await Promise.all([heightAnimation.finished, contentAnimation.finished])
      details.open = false
    }
    details.dataset.isAnimating = 'false'
  }

  checkIfTruncationNeeded (content, toggleButton) {
    if (!content.textContent || content.textContent.trim().length === 0) {
      toggleButton.style.display = 'none'
      const gradient = this.querySelector('[data-description-gradient]')
      if (gradient) {
        gradient.style.display = 'none'
      }
      return
    }

    const clone = content.cloneNode(true)
    clone.style.cssText = `
      position: absolute;
      visibility: hidden;
      height: auto;
      max-height: none;
      -webkit-line-clamp: unset;
      -webkit-box-orient: unset;
      display: block;
      width: ${content.offsetWidth}px;
    `
    content.parentNode.appendChild(clone)
    const fullHeight = clone.offsetHeight
    content.parentNode.removeChild(clone)
    const collapsedHeight = content.offsetHeight
    const needsTruncation = fullHeight > collapsedHeight

    if (!needsTruncation) {
      toggleButton.style.display = 'none'
      const gradient = this.querySelector('[data-description-gradient]')
      if (gradient) {
        gradient.style.display = 'none'
      }
    }
  }

  announceToScreenReader (message) {
    const announcement = document.createElement('div')
    announcement.setAttribute('aria-live', 'polite')
    announcement.setAttribute('aria-atomic', 'true')
    announcement.className = 'sr-only'
    announcement.textContent = message
    document.body.appendChild(announcement)
    setTimeout(() => {
      document.body.removeChild(announcement)
    }, 1000)
  }

  initProductIconBlocks () {
    const productIconBlocks = this.querySelectorAll('.product-icon-blocks__item--modal-trigger')
    productIconBlocks.forEach(block => {
      block.addEventListener('click', () => {
        this.modalImage = block.dataset.modalImage
        this.modalVideo = block.dataset.modalVideo
        this.modalVideoAutoplay = block.dataset.modalVideoAutoplay === 'true'
        this.modalContent = block.dataset.modalContent
        this.buildModal(block)
      })
    })
    this.initFinanceIconBlocks()
  }

  initFinanceIconBlocks () {
    const financeBlocks = this.querySelectorAll('.product-icon-blocks__item--finance-trigger')
    if (!financeBlocks.length) return

    financeBlocks.forEach(block => {
      block.addEventListener('click', (event) => {
        event.preventDefault()
        this.openFinanceFromIconBlock(block)
      })
      block.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return
        event.preventDefault()
        this.openFinanceFromIconBlock(block)
      })
    })

    document.addEventListener('finance:resolved', () => this.updateFinanceIconBlocks(financeBlocks))
    this.updateFinanceIconBlocks(financeBlocks)
  }

  getFinanceTrigger () {
    const productPrice = document.querySelector('.product-price')
    if (!productPrice) return null

    const klarnaContainer = productPrice.querySelector('.klarna-app-block-container')
    if (klarnaContainer && !klarnaContainer.classList.contains('hidden')) {
      const klarnaCta = klarnaContainer.querySelector('klarna-placement')?.shadowRoot?.querySelector('[part="osm-cta"]')
      if (klarnaCta) return klarnaCta
    }

    const financeModule = productPrice.querySelector('finance-module')
    const financeContainer = financeModule?.closest('.finance-section-container')
    if (financeModule && (!financeContainer || !financeContainer.classList.contains('hidden'))) {
      return financeModule
    }

    return null
  }

  openFinanceFromIconBlock (block) {
    let attempts = 0
    const maxAttempts = 15
    block.setAttribute('loading', '')

    const tryOpen = () => {
      const trigger = this.getFinanceTrigger()
      if (trigger) {
        block.removeAttribute('loading')
        trigger.click()
        return
      }
      attempts++
      if (attempts >= maxAttempts) {
        block.removeAttribute('loading')
        return
      }
      setTimeout(tryOpen, 100)
    }

    tryOpen()
  }

  updateFinanceIconBlocks (financeBlocks) {
    const productPrice = document.querySelector('.product-price')
    const geoipHandler = productPrice?.querySelector('klarna-geoip-handler')
    const pending = geoipHandler?.classList.contains('hidden') === true
    const klarnaVisible = !!geoipHandler && !geoipHandler.querySelector('.klarna-app-block-container')?.classList.contains('hidden')
    const financeContainer = productPrice?.querySelector('.finance-section-container')
    const financeVisible = !!productPrice?.querySelector('finance-module') && !financeContainer?.classList.contains('hidden')
    const available = pending || klarnaVisible || financeVisible

    financeBlocks.forEach(block => {
      block.classList.toggle('cursor-pointer', available)
      block.classList.toggle('product-icon-blocks__item--finance-unavailable', !available)
      if (available) {
        block.setAttribute('role', 'button')
        block.setAttribute('tabindex', '0')
      } else {
        block.removeAttribute('role')
        block.removeAttribute('tabindex')
      }
      const infoIcon = block.querySelector('.product-icon-blocks__text-info-icon')
      if (infoIcon) {
        infoIcon.style.display = available ? '' : 'none'
      }
    })
  }

  buildModal (block) {
    block.setAttribute('loading', '')
    const hasImage = this.modalImage && this.modalImage.trim() !== '' && this.modalImage.trim() !== 'null'
    const hasVideo = this.modalVideo && this.modalVideo.trim() !== '' && this.modalVideo.trim() !== 'null'
    const ensureModalThen = (fn) => {
      const waitForModal = () => {
        if (!this.modal) {
          requestAnimationFrame(waitForModal)
          return
        }
        fn()
      }
      waitForModal()
    }
    if (!hasImage && !hasVideo) {
      ensureModalThen(() => {
        this.setModalContent()
        setTimeout(() => {
          this.openModal()
          block.removeAttribute('loading')
        }, 100)
      })
      return
    }
    const mediaToLoad = []
    if (hasImage) {
      const img = new Image()
      img.src = this.modalImage
      img.classList.add('w-full', 'h-auto')
      mediaToLoad.push({ element: img, type: 'image' })
    }
    if (hasVideo) {
      if (this.modalVideoAutoplay) {
        const video = document.createElement('video')
        video.src = this.modalVideo
        video.classList.add('w-full', 'h-auto')
        video.loop = true
        video.muted = true
        video.playsinline = true
        video.autoplay = true
        mediaToLoad.push({ element: video, type: 'video' })
      } else {
        this.modalVideoNeedsControls = true
        mediaToLoad.push({ element: null, type: 'modal-video-placeholder' })
      }
    }
    Promise.all(mediaToLoad.map(media => {
      return new Promise((resolve, reject) => {
        if (media.type === 'video') {
          media.element.addEventListener('canplay', resolve)
          media.element.addEventListener('error', reject)
          setTimeout(resolve, 3000)
        } else if (media.type === 'modal-video') {
          const videoElement = media.element.querySelector('video')
          if (videoElement) {
            videoElement.addEventListener('canplay', resolve)
            videoElement.addEventListener('error', reject)
            setTimeout(resolve, 3000)
          } else {
            resolve()
          }
        } else if (media.type === 'modal-video-placeholder') {
          resolve()
        } else {
          media.element.onload = resolve
          media.element.onerror = reject
        }
      })
    }))
      .then(() => {
        this.loadedMedia = mediaToLoad
        ensureModalThen(() => {
          this.setModalContent()
          setTimeout(() => {
            this.openModal()
            block.removeAttribute('loading')
          }, 100)
        })
      })
      .catch(() => {
        ensureModalThen(() => {
          this.setModalContent()
          setTimeout(() => {
            this.openModal()
            block.removeAttribute('loading')
          }, 100)
        })
      })
  }

  openModal () {
    window.dispatchEvent(
      new CustomEvent('open:modal', {
        detail: {
          width: 'min(600px, 100svw)',
          padding: 'none',
          headerClassAdd: ['absolute', 'h-20', 'top-0', 'left-0', 'right-0', 'z-20', 'block'],
          headerClassRemove: ['border-b', 'mb-4', 'pb-2', 'grid'],
          closeClassAdd: ['absolute', 'top-4', 'right-4', 'z-20'],
          closeClassRemove: [],
        },
      }),
    )
    const checkModalReady = () => {
      if (!this.modal) {
        requestAnimationFrame(checkModalReady)
        return
      }
      this.setModalContent()
    }
    checkModalReady()
  }

  setModalContent () {
    const hasImage = this.modalImage && this.modalImage.trim() !== '' && this.modalImage.trim() !== 'null'
    const hasVideo = this.modalVideo && this.modalVideo.trim() !== '' && this.modalVideo.trim() !== 'null'
    this.modalBody = this.modal.querySelector('.reusable-modal__body')
    let mediaElement = ''
    if (this.loadedMedia && this.loadedMedia.length > 0) {
      const media = this.loadedMedia[0]
      if (media.type === 'modal-video' && media.element) {
        mediaElement = `
          <div class="icon-block-modal__media">
            ${media.element.outerHTML}
          </div>
        `
      } else if (media.element) {
        mediaElement = `
          <div class="icon-block-modal__media">
            ${media.element.outerHTML}
          </div>
        `
      }
    } else if (hasVideo) {
      if (this.modalVideoAutoplay) {
        mediaElement = `
          <div class="icon-block-modal__media">
            <video loop muted playsinline autoplay class="w-full h-auto">
              <source src="${this.modalVideo}" type="video/mp4">
              Your browser does not support the video tag.
            </video>
          </div>
        `
      } else {
        const modalVideoElement = document.createElement('modal-video')
        modalVideoElement.setAttribute('data-source', this.modalVideo)
        modalVideoElement.classList.add('video-not-started')
        if (this.modalImage && this.modalImage.trim() !== '' && this.modalImage.trim() !== 'null') {
          const posterImg = document.createElement('img')
          posterImg.src = this.modalImage
          posterImg.alt = ''
          modalVideoElement.appendChild(posterImg)
        }
        mediaElement = `
          <div class="icon-block-modal__media">
            ${modalVideoElement.outerHTML}
          </div>
        `
      }
    } else if (hasImage) {
      mediaElement = `
        <div class="icon-block-modal__media">
          <img src="${this.modalImage}" alt="" class="w-full h-auto">
        </div>
      `
    }
    let processedContent = this.modalContent || ''
    try {
      // Convert '+' back to spaces (common from URL-encoded content)
      // Then decode percent-encoded sequences if present.
      processedContent = processedContent.replace(/\+/g, '%20')
      processedContent = decodeURIComponent(processedContent)
    } catch (e) {
      // If decode fails, at least normalize '+' to spaces for readability
      processedContent = processedContent.replace(/\+/g, ' ')
    }
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = processedContent

    const modalContent = `
      <div class="icon-block-modal flex flex-col">
        ${mediaElement}
        <div class="icon-block-modal__content tb-in-page-content tb-list-fix px-6 py-6">${tempDiv.innerHTML}</div>
      </div>
    `
    this.modalBody.innerHTML = modalContent
    this.loadedMedia = null
  }

  get modal () {
    const modalElement = document.querySelector('.reusable-modal')
    if (!modalElement) {
      console.error('Modal element not found.')
    }
    return modalElement
  }
}
if (!customElements.get('main-product')) {
  customElements.define('main-product', MainProduct)
}

if (!customElements.get('klarna-geoip-handler')) {
  customElements.define('klarna-geoip-handler', KlarnaGeoIPHandler)
}
/* eslint-enable no-unused-vars */
